// eslint-disable-next-line import/no-cycle
import apiClient from '../../api';
import { isObj, isArr } from '@/utils';
// eslint-disable-next-line import/no-cycle
import store from '..';
import initialState from '../initialState';

const creatorProgramApi = apiClient.merchy.creatorProgram

/**
 * @description Is valid
 * @param items
 * @returns {boolean}
 */
const isValid = (items) => {
  const {
    activeBillingAccount,
    activeBusiness,
    businesses,
    businessesMeta,
    billingAccounts,
    billingAccountsMeta
  } = items;

  return isObj(activeBillingAccount)
    && isObj(activeBusiness)
    && isArr(businesses)
    && isObj(businessesMeta)
    && isArr(billingAccounts)
    && isObj(billingAccountsMeta)
};

/**
 * @description Init state
 * @param initialState
 * @returns {*}
 */
const initState = initialState => {
  if (!isValid(initialState)) {
    throw Error('Invalid initial creator program state');
  }

  const {
    activeBillingAccount,
    activeBusiness,
    businesses,
    businessesMeta,
    billingAccounts,
    billingAccountsMeta
  } = initialState;

  return {
    activeBillingAccount,
    activeBusiness,
    businesses,
    businessesMeta,
    billingAccounts,
    billingAccountsMeta
  };
};

/**
 * @description Getters
 * @type {*}
 */
export const getters = {
  activeBillingAccount: ({ activeBillingAccount }) => activeBillingAccount,
  activeBusiness: ({ activeBusiness }) => activeBusiness,
  businesses: ({ businesses }) => businesses,
  businessesMeta: ({ businessesMeta }) => businessesMeta,
  billingAccounts: ({ billingAccounts }) => billingAccounts,
  billingAccountsMeta: ({ billingAccountsMeta }) => billingAccountsMeta
};

/**
 * @description Handle alerts
 * @param data
 * @param alertType
 */
const handleAlerts = (data, alertType = 'error') => (
  store.dispatch('alerts/set', {
    data,
    alertType
  })
);

const actions = {
  createBusiness: (_, query) => (
    creatorProgramApi.createBusiness(query).then((res) => {
      handleAlerts({
        data: {
          message: 'Business created successfully.'
        }
      }, 'success')

      return res;
    }).catch(e => Promise.reject(e))
  ),
  createBillingAccount: ({ commit, state }, query) => (
    creatorProgramApi.createBillingAccount(query).then((res) => {
      handleAlerts({
        data: {
          message: 'Billing account created successfully.'
        }
      }, 'success')

      return res;
    }).catch(e => Promise.reject(e))
  ),
  updateBusiness: (_, query) => (
    creatorProgramApi.updateBusiness(query).then((res) => {
      handleAlerts({
        data: {
          message: 'Business updated successfully.'
        }
      }, 'success')

      return res;
    }).catch(e => Promise.reject(e))
  ),
  updateBillingAccount: (_, query) => (
    creatorProgramApi.updateBillingAccount(query).then((res) => {
      handleAlerts({
        data: {
          message: 'Billing account updated successfully.'
        }
      }, 'success')

      return res;
    }).catch(e => Promise.reject(e))
  ),
  getBusiness: ({ commit, state }, query) => (
    creatorProgramApi.getBusiness(query).then((res) => {
      const nextActiveBusiness = {
        ...res.data.data
      };

      const nextState = {
        ...state,
        activeBusiness: nextActiveBusiness
      };

      commit('SET', nextState);

      return nextActiveBusiness;
    }).catch(e => Promise.reject(e))
  ),
  getBusinesses: ({ commit, state }, query) => (
    creatorProgramApi.getBusinesses(query).then((res) => {
      const {data, meta} = res.data;

      const nextItems = {
        businesses: data,
        businessesMeta: meta
      };

      const nextState = {
        ...state,
        ...nextItems
      };

      commit('SET', nextState);

      return nextItems;
    }).catch(e => Promise.reject(e))
  ),
  /**
   * Looks businesses up by id and returns them without committing: the
   * cached `businesses` listing belongs to whatever the user last searched,
   * and this is used for a one-off lookup (e.g. a restored URL filter).
   */
  getBusinessesByIds: (context, { ids }) => (
    creatorProgramApi.getBusinesses({ ids, per_page: ids.length }).then((res) => res.data.data)
  ),
  getBillingAccount: ({ commit, state }, query) => (
    creatorProgramApi.getBillingAccount(query).then((res) => {
      const nextActiveBillingAccount = {
        ...res.data.data
      };

      const nextState = {
        ...state,
        activeBillingAccount: nextActiveBillingAccount
      };

      commit('SET', nextState);

      return nextActiveBillingAccount;
    }).catch(e => Promise.reject(e))
  ),
  getBillingAccounts: ({ commit, state }, query) => (
    creatorProgramApi.getBillingAccounts(query).then((res) => {
      const {data, meta} = res.data;

      const nextItems = {
        billingAccounts: data,
        billingAccountsMeta: meta
      };

      const nextState = {
        ...state,
        ...nextItems
      };

      commit('SET', nextState);

      return nextItems;
    }).catch(e => Promise.reject(e))
  ),
  setActiveBusiness: ({ commit, state }, business) => {
    commit('SET', {
      ...state,
      activeBusiness: business
    });
  },
  reset: ({ commit }) => (
    commit('SET', initialState.creatorProgram)
  ),
  set: ({ commit }, creatorProgram) => {
    commit('SET', creatorProgram);
  }
};

const mutations = {
  SET(state, creatorProgram) {
    /* eslint-disable no-param-reassign */
    state.activeBillingAccount = creatorProgram.activeBillingAccount;
    state.activeBusiness = creatorProgram.activeBusiness;
    state.businesses = creatorProgram.businesses;
    state.businessesMeta = creatorProgram.businessesMeta;
    state.billingAccounts = creatorProgram.billingAccounts;
    state.billingAccountsMeta = creatorProgram.billingAccountsMeta;
  }
};

export default initialState => ({
  namespaced: true,
  state: initState(initialState),
  getters,
  actions,
  mutations,
});
